import {
  SimpleGrid,
  Box,
  Image,
  Text,
  AspectRatio,
  ColorPicker,
  parseColor,
  Grid,
  GridItem,
  Input,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { VscLock } from "react-icons/vsc";
import { useNavigate } from "react-router-dom";
import { ReactComponent as CheckIcon } from "../../Icons/checkMarkFilled.svg";
import { useRoomStore } from "../../store";
import { materialOptions, roomColors } from "../../constants/materialOptions";
import { useEffect, useMemo, useRef, useState } from "react";
import VideoUploadForWalls from "./VideoUploadForWalls";
import { useLicenseStore } from "../../store/licenseStore";
import ImageUploadForWalls from "./ImageUploadForWalls";

const RoomMaterialSelector = () => {
  const navigate = useNavigate();
  const setMaterialId = useRoomStore((s) => s.setMaterialId);
  const setMaterialType = useRoomStore((s) => s.setMaterialType);
  const materialId = useRoomStore((s) => s.materialId);
  const setMaterialOptions = useRoomStore((s) => s.setMaterialOptions);
  const shaderOptions = useRoomStore((s) => s.shaderOptions);
  const setShaderOptions = useRoomStore((s) => s.setShaderOptions);

  const hasLicense = useLicenseStore((s) => s.hasLicense);
  const isShopItem = useLicenseStore((s) => s.isShopItem);

  const isShaderLocked = (option) => {
    if (option.type !== 'shader') return false;
    const shopId = `shader_${option.value}`;
    return isShopItem(shopId) && !hasLicense(shopId);
  };

  const [selectedType, setSelectedType] = useState("basic");
  const [selected, setSelected] = useState(materialOptions[0]);
  const [selectedColor, setSelectedColor] = useState(null);
  const prevMaterialIdRef = useRef(materialId);

  // Collect unique types from options
  const materialTypes = useMemo(() => {
    return [...new Set(materialOptions.map((opt) => opt.type))];
  }, []);

  // Get current material options (for preserving video src)
  const currentMaterialOptions = useRoomStore((s) => s.materialOptions);

  // Keep selected in sync with store
  useEffect(() => {
    const option = materialOptions.find(
      (opt) => opt.value === materialId.toString()
    );
    if (option) {
      setSelected(option);
      setSelectedType(option.type);
      setMaterialType(option.type);
      // For video/image type, preserve the src if it exists
      if ((option.type === "video" || option.type === "image") && currentMaterialOptions?.src) {
        // Don't reset - keep the uploaded URL
      } else {
        setMaterialOptions({});
      }
      // Only reset shader options when actually switching to a different material,
      // not on re-mount with the same material (which would wipe custom colors)
      const materialChanged = prevMaterialIdRef.current !== materialId;
      prevMaterialIdRef.current = materialId;
      if (materialChanged) {
        setShaderOptions({});
      }
      if (option?.materialProps?.color)
        setSelectedColor(parseColor(option?.materialProps?.color));
      else {
        setSelectedColor(null);
      }
    }
  }, [materialId, setMaterialOptions, setMaterialType, setShaderOptions, currentMaterialOptions?.src]);

  const handleTypeChange = (type) => {
    setSelectedType(type);

    // For video/image type, only change the actual material if there's already content uploaded
    // Otherwise just show the upload UI without changing the walls
    if (type === "video") {
      if (currentMaterialOptions?.src) {
        setMaterialType("video");
        setMaterialId("20");
        const videoOption = materialOptions.find((opt) => opt.type === "video");
        if (videoOption) setSelected(videoOption);
      }
      return;
    }
    if (type === "image") {
      if (currentMaterialOptions?.src) {
        setMaterialType("image");
        setMaterialId("21");
        const imageOption = materialOptions.find((opt) => opt.type === "image");
        if (imageOption) setSelected(imageOption);
      }
      return;
    }

    // For non-video types, change material immediately
    setMaterialType(type);
    const firstOfType = materialOptions.find((opt) => opt.type === type);
    if (firstOfType) {
      setMaterialId(firstOfType.value);
      setSelected(firstOfType);
    }
  };

  const handleSelectMaterial = (option) => {
    if (isShaderLocked(option)) {
      navigate('/collection');
      return;
    }
    setMaterialId(option.value);
    setMaterialType(option.type);
    setSelected(option);
  };

  // Filter to show basic, shader, video, and image types
  const typeButtons = materialTypes.filter(
    (type) => type === "basic" || type === "shader" || type === "video" || type === "image"
  );

  // Display names for type buttons
  const typeDisplayNames = {
    basic: "Basic",
    shader: "Animated",
    video: "Video",
    image: "Image",
  };

  return (
    <Box>
      {/* Wall Type Selection */}
      <Text fontSize="sm" mb={3} color="gray.300" fontWeight="medium">
        Select Wall Type:
      </Text>
      <Grid templateColumns="repeat(4, 1fr)" gap={3} mb={6}>
        {typeButtons.map((type) => {
          const isActive = selectedType === type;
          return (
            <GridItem key={type}>
              <Box
                onClick={() => handleTypeChange(type)}
                cursor="pointer"
                height="40px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                border="2px solid"
                borderColor={isActive ? "#26EF96" : "gray.600"}
                bg={isActive ? "rgba(38, 239, 150, 0.1)" : "gray.800"}
                _hover={{
                  bg: isActive ? "rgba(38, 239, 150, 0.15)" : "gray.700",
                  borderColor: isActive ? "#26EF96" : "gray.500",
                }}
                transition="all 0.2s"
                borderRadius="0"
              >
                <Text
                  color={isActive ? "#26EF96" : "white"}
                  fontWeight={isActive ? "bold" : "medium"}
                  fontSize="md"
                >
                  {typeDisplayNames[type] || type}
                </Text>
              </Box>
            </GridItem>
          );
        })}
      </Grid>

      {/* Material Options Grid - hidden for video and image types */}
      {selectedType !== "video" && selectedType !== "image" && (
        <>
          <Text fontSize="sm" mb={3} color="gray.300" fontWeight="medium">
            Select Material:
          </Text>
          <SimpleGrid columns={4} spacing={5} mt={3}>
            {materialOptions
              .filter((opt) => opt.type === selectedType)
              .map((option) => {
                const isActive = selected.value === option.value;
                const locked = isShaderLocked(option);
                return (
                  <Box
                    key={option.value}
                    onClick={() => handleSelectMaterial(option)}
                    cursor={locked ? "not-allowed" : "pointer"}
                    display="flex"
                    flexDir="column"
                    alignItems="center"
                    justifyContent="flex-start"
                    opacity={locked ? 0.7 : 1}
                  >
                    <Box position="relative" w="100%" maxW="100px">
                      <AspectRatio ratio={1} w="100%">
                        <Image
                          src={
                            option?.image ||
                            "https://placehold.co/60x60/orange/white"
                          }
                          alt={option.label}
                          objectFit="cover"
                          borderRadius="0"
                          border={
                            isActive ? "solid #26EF96 3px" : "solid transparent 3px"
                          }
                        />
                      </AspectRatio>

                      {isActive && !locked && (
                        <Box
                          position="absolute"
                          inset={0}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          bg="rgba(0,0,0,0.2)"
                          borderRadius="0"
                        >
                          <CheckIcon width="24px" height="24px" />
                        </Box>
                      )}

                      {locked && (
                        <Box
                          position="absolute"
                          inset={0}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          bg="blackAlpha.600"
                          borderRadius="0"
                        >
                          <Icon as={VscLock} color="white" boxSize={4} />
                        </Box>
                      )}
                    </Box>
                    <Text
                      mt={1}
                      fontSize="xs"
                      textAlign="center"
                      noOfLines={1}
                      maxW="100px"
                    >
                      {option.label}
                    </Text>
                  </Box>
                );
              })}
          </SimpleGrid>
        </>
      )}

      {/* Color Picker for Basic Type */}
      {selectedType === "basic" && selectedColor && (
        <Box mt={4}>
          <Text fontSize="sm" mb={3} color="gray.300" fontWeight="medium">
            Customize Color:
          </Text>
          <Box w={{ base: "80%", md: "100%" }} mx={{ base: "auto", md: "0" }}>
            <ColorPicker.Root
              open
              defaultValue={parseColor("#000")}
              value={selectedColor}
              onValueChange={(c) => {
                setSelectedColor(c.value);
                setMaterialOptions({ color: c.valueAsString });
              }}
            >
              <ColorPicker.Content
                width={"100%"}
                animation="none"
                shadow="none"
                padding="0"
                gap="0"
                borderRadius="0"
              >
                <ColorPicker.Area borderRadius="0" />
                <ColorPicker.ChannelSlider channel="hue" borderRadius="0" />
              </ColorPicker.Content>
              <ColorPicker.SwatchGroup gap={1} justifyContent="center" mt={3}>
                {roomColors.map((item) => (
                  <ColorPicker.SwatchTrigger key={item} value={item}>
                    <ColorPicker.Swatch value={item} borderRadius="0">
                      <ColorPicker.SwatchIndicator boxSize="2" bg="white" />
                    </ColorPicker.Swatch>
                  </ColorPicker.SwatchTrigger>
                ))}
              </ColorPicker.SwatchGroup>
            </ColorPicker.Root>
          </Box>
        </Box>
      )}

      {/* Color Pickers for Shader Type */}
      {selectedType === "shader" && selected?.shader?.customizable && (
        <Box mt={4}>
          <Text fontSize="sm" mb={3} color="gray.300" fontWeight="medium">
            Customize Colors:
          </Text>
          {selected.shader.customizable.map((colorConfig) => (
            <HStack key={colorConfig.uniform} mb={3} spacing={3}>
              <Input
                type="color"
                value={shaderOptions[colorConfig.uniform] || colorConfig.default}
                onChange={(e) => {
                  setShaderOptions({
                    ...shaderOptions,
                    [colorConfig.uniform]: e.target.value,
                  });
                }}
                width="50px"
                height="36px"
                padding="2px"
                border="2px solid"
                borderColor="gray.600"
                borderRadius="0"
                cursor="pointer"
                _hover={{ borderColor: "gray.500" }}
              />
              <Text fontSize="sm" color="gray.300">
                {colorConfig.label}
              </Text>
            </HStack>
          ))}
        </Box>
      )}

      {/* Speed Slider for Shader Type */}
      {selectedType === "shader" && (
        <Box mt={4}>
          <HStack mb={2} justifyContent="space-between">
            <Text fontSize="sm" color="gray.300" fontWeight="medium">
              Speed
            </Text>
            <Text fontSize="xs" color="gray.400">
              {(shaderOptions.speedMultiplier ?? 1.0).toFixed(2)}
            </Text>
          </HStack>
          <input
            type="range"
            min="0"
            max="2"
            step="0.05"
            value={shaderOptions.speedMultiplier ?? 1.0}
            onChange={(e) => {
              setShaderOptions({
                ...shaderOptions,
                speedMultiplier: parseFloat(e.target.value),
              });
            }}
            style={{
              width: "100%",
              height: "24px",
              cursor: "pointer",
              accentColor: "#26EF96",
            }}
          />
        </Box>
      )}

      {/* Video Upload for Video Type */}
      {selectedType === "video" && <VideoUploadForWalls />}

      {/* Image Upload for Image Type */}
      {selectedType === "image" && <ImageUploadForWalls />}
    </Box>
  );
};

export default RoomMaterialSelector;
